import { create } from "zustand";

import type {
  ConnectorOAuthAttempt,
  ConnectorOAuthAttemptEntry,
  ConnectorOAuthAutoContinuationState,
} from "@/models/connectorOAuth";

interface ConnectorOAuthStore {
  entriesByAttemptId: Record<string, ConnectorOAuthAttemptEntry>;
  upsertAttempt: (
    attempt: ConnectorOAuthAttempt,
    options?: {
      startedInThisTab?: boolean;
      lastConfirmedAt?: number;
      autoContinuationState?: ConnectorOAuthAutoContinuationState;
    },
  ) => void;
  setAutoContinuationState: (
    attemptPublicId: string,
    state: ConnectorOAuthAutoContinuationState,
  ) => void;
  reset: () => void;
}

export const useConnectorOAuthStore = create<ConnectorOAuthStore>((set) => ({
  entriesByAttemptId: {},

  upsertAttempt: (attempt, options) => {
    set((state) => {
      const current = state.entriesByAttemptId[attempt.attemptPublicId];
      return {
        entriesByAttemptId: {
          ...state.entriesByAttemptId,
          [attempt.attemptPublicId]: {
            attempt,
            startedInThisTab:
              options?.startedInThisTab ?? current?.startedInThisTab ?? false,
            lastConfirmedAt:
              options?.lastConfirmedAt ?? current?.lastConfirmedAt,
            autoContinuationState:
              options?.autoContinuationState ??
              current?.autoContinuationState ??
              "none",
          },
        },
      };
    });
  },

  setAutoContinuationState: (attemptPublicId, autoContinuationState) => {
    set((state) => {
      const current = state.entriesByAttemptId[attemptPublicId];
      if (!current || current.autoContinuationState === autoContinuationState) {
        return state;
      }
      return {
        entriesByAttemptId: {
          ...state.entriesByAttemptId,
          [attemptPublicId]: { ...current, autoContinuationState },
        },
      };
    });
  },

  reset: () => set({ entriesByAttemptId: {} }),
}));

/** Returns the current OAuth entry without subscribing a React component. */
export function getConnectorOAuthEntry(
  attemptPublicId: string,
): ConnectorOAuthAttemptEntry | undefined {
  return useConnectorOAuthStore.getState().entriesByAttemptId[attemptPublicId];
}
